import { useEffect, useMemo, useState } from 'react';
import type { AgreementOverageRecipient } from '@catalyst-psa/shared';

import { mutationError } from '../../api/client.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { Spinner } from '../../components/ui/Spinner.js';
import { formatDateTime } from '../../utils/date.js';
import { useAgreementOverageAlert, useSetAgreementOverageRecipients } from './api.js';

interface AgreementOverageAlertCardProps {
  agreementId: string;
  canEdit: boolean;
}

/** Sorted ids, so a selection can be compared to what was loaded. */
function keyOf(ids: Iterable<string>): string {
  return [...ids].sort().join(',');
}

function selectedIds(recipients: readonly AgreementOverageRecipient[]): Set<string> {
  return new Set(recipients.filter((r) => r.selected).map((r) => r.id));
}

/**
 * Picks who is emailed the first time this agreement's Catalyst+ hours go into overage
 * for a month.
 *
 * Contacts are backed by the "Catalyst+ notifications" checkbox on the contact record —
 * ticking someone here ticks it there, and vice versa — so there is only ever one list to
 * keep straight. Internal members are chosen per agreement.
 */
export function AgreementOverageAlertCard({
  agreementId,
  canEdit,
}: AgreementOverageAlertCardProps): JSX.Element {
  const query = useAgreementOverageAlert(agreementId);
  const save = useSetAgreementOverageRecipients(agreementId);
  const settings = query.data;

  const [contactIds, setContactIds] = useState<Set<string>>(new Set());
  const [memberIds, setMemberIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!settings) return;
    setContactIds(selectedIds(settings.contacts));
    setMemberIds(selectedIds(settings.members));
  }, [settings]);

  const dirty = useMemo(() => {
    if (!settings) return false;
    return (
      keyOf(contactIds) !== keyOf(selectedIds(settings.contacts)) ||
      keyOf(memberIds) !== keyOf(selectedIds(settings.members))
    );
  }, [settings, contactIds, memberIds]);

  const toggle = (
    setter: React.Dispatch<React.SetStateAction<Set<string>>>,
    id: string,
  ): void => {
    setter((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const totalSelected = contactIds.size + memberIds.size;
  const error = mutationError(save.error, 'Could not save the recipients.');

  return (
    <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
      <header className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-200 px-4 py-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Overage alert recipients</h3>
          <p className="mt-0.5 text-xs text-slate-500">
            Emailed once a month, the first time this agreement goes past its included hours.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {settings?.currentPeriodAlert ? (
            <Badge tone="amber">Sent this month</Badge>
          ) : totalSelected === 0 ? (
            <Badge tone="gray">No recipients</Badge>
          ) : (
            <Badge tone="green">{`${totalSelected} recipient${totalSelected === 1 ? '' : 's'}`}</Badge>
          )}
          {canEdit ? (
            <Button
              size="sm"
              disabled={!dirty}
              isLoading={save.isPending}
              onClick={() =>
                save.mutate({ contactIds: [...contactIds], memberIds: [...memberIds] })
              }
            >
              Save recipients
            </Button>
          ) : null}
        </div>
      </header>

      <div className="p-4">
        {query.isLoading ? (
          <div className="flex justify-center py-6">
            <Spinner />
          </div>
        ) : query.isError ? (
          <p className="text-sm text-rose-600">Could not load the recipient list.</p>
        ) : settings ? (
          <>
            {error ? (
              <p className="mb-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                {error}
              </p>
            ) : null}

            {settings.lastAlert ? (
              <p className="mb-4 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                Last alert sent {formatDateTime(settings.lastAlert.sentAt)} for{' '}
                {settings.lastAlert.period} to {settings.lastAlert.recipients.length} recipient
                {settings.lastAlert.recipients.length === 1 ? '' : 's'}
                {settings.lastAlert.overageHours
                  ? ` (${settings.lastAlert.overageHours} overage hours at the time)`
                  : ''}
                .
              </p>
            ) : null}

            <div className="grid gap-6 md:grid-cols-2">
              <RecipientList
                title="Customer contacts"
                note="Mirrors the “Catalyst+ notifications” checkbox on each contact record."
                recipients={settings.contacts}
                selected={contactIds}
                disabled={!canEdit}
                onToggle={(id) => toggle(setContactIds, id)}
                emptyMessage="This customer has no active contacts."
              />
              <RecipientList
                title="Internal members"
                note="Chosen per agreement — these people get the same email."
                recipients={settings.members}
                selected={memberIds}
                disabled={!canEdit}
                onToggle={(id) => toggle(setMemberIds, id)}
                emptyMessage="No active members."
              />
            </div>

            {totalSelected === 0 ? (
              <p className="mt-4 text-xs text-slate-500">
                With nobody selected no alert is sent. Adding someone later in the month still
                triggers the alert if the agreement is already in overage.
              </p>
            ) : null}
          </>
        ) : null}
      </div>
    </section>
  );
}

function RecipientList({
  title,
  note,
  recipients,
  selected,
  disabled,
  onToggle,
  emptyMessage,
}: {
  title: string;
  note: string;
  recipients: readonly AgreementOverageRecipient[];
  selected: Set<string>;
  disabled: boolean;
  onToggle: (id: string) => void;
  emptyMessage: string;
}): JSX.Element {
  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{title}</p>
      <p className="mb-2 mt-0.5 text-xs text-slate-500">{note}</p>
      {recipients.length === 0 ? (
        <p className="text-sm text-slate-500">{emptyMessage}</p>
      ) : (
        <ul className="max-h-64 space-y-1 overflow-y-auto pr-1">
          {recipients.map((recipient) => (
            <li key={recipient.id}>
              <label
                className={`flex items-start gap-2 rounded px-1 py-1 text-sm ${
                  disabled ? '' : 'cursor-pointer hover:bg-slate-50'
                }`}
              >
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 rounded border-slate-300 text-brand-600"
                  checked={selected.has(recipient.id)}
                  disabled={disabled || !recipient.email}
                  onChange={() => onToggle(recipient.id)}
                />
                <span className="min-w-0">
                  <span className="block truncate text-slate-800">{recipient.name}</span>
                  <span className="block truncate text-xs text-slate-500">
                    {recipient.email ?? 'No email address — cannot be emailed'}
                  </span>
                </span>
              </label>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
